import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaEyeSlash, FaEye as FaEyeIcon, FaTag, FaPlus, FaTimes, FaSitemap, FaChevronDown, FaChevronRight, FaMapMarkedAlt } from 'react-icons/fa';
import { productListApiService } from '../../../service/admin/productApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import Pagination from '../../../component/library/pagination';
import Loader from '../../../component/common/loader';
import './style.css';

const Tags = () => {
  const message = useMessage();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [editedTagValue, setEditedTagValue] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'visible', 'hidden'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'parent'
  const [totalTags, setTotalTags] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Hierarchy state
  const [hierarchies, setHierarchies] = useState([]);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [childTagSearch, setChildTagSearch] = useState('');
  const [hierarchyModalTags, setHierarchyModalTags] = useState([]);
  const [hierarchyModalTagsLoading, setHierarchyModalTagsLoading] = useState(false);

  // Fetch a specific page of tags
  const fetchTagsPage = async (page, limit = itemsPerPage, search = searchQuery) => {
    try {
      const response = await productListApiService.getAllTags(page, limit, search);
      if (response?.status?.code === 200) {
        const { tags: pageTags, pagination } = response.data || {};
        
        return {
          tags: pageTags || [],
          page: page,
          pagination: pagination || null
        };
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
        return null;
      } else {
        message.showToast(response?.error?.message || 'Failed to fetch tags', ToastTypes.ERROR);
        return null;
      }
    } catch (error) {
      console.error(`Failed to fetch tags page ${page}:`, error);
      return null;
    }
  };

  // Load only the current page from the server (no preloading other pages)
  const fetchTags = async () => {
    setLoading(true);
    try {
      const pageData = await fetchTagsPage(currentPage, itemsPerPage, searchQuery);

      if (pageData) {
        setTags(pageData.tags);

        if (pageData.pagination) {
          setTotalTags(pageData.pagination.totalTags || 0);
          setTotalPages(pageData.pagination.totalPages || 0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      message.showToast(error?.message || 'Error loading tags', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchies = async () => {
    try {
      // Fetch from new ParentTags collection
      const response = await productListApiService.getParentTags();
      if (response?.status?.code === 200) {
        // Convert ParentTags format to hierarchy format for display
        const parentTags = response.data?.parentTags || [];
        const hierarchies = parentTags.map(pt => ({
          parentTag: pt.parentTag,
          childTags: pt.tags || []
        }));
        setHierarchies(hierarchies);
      }
    } catch (error) {
      console.error('Failed to fetch hierarchies:', error);
    }
  };

  useEffect(() => {
    fetchHierarchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  // Load the current page when user navigates or search changes
  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchQuery]);

  // When hierarchy modal is open with a parent selected, fetch tags similar to that parent
  useEffect(() => {
    if (!showHierarchyModal || !selectedParent.trim()) {
      setHierarchyModalTags([]);
      return;
    }
    let cancelled = false;
    setHierarchyModalTagsLoading(true);
    const searchTerm = selectedParent.trim();
    // Use parent tag (and its prefix before first hyphen) so we get e.g. 745881261331-* tags
    const prefix = searchTerm.includes('-') ? searchTerm.split('-')[0] : searchTerm;
    productListApiService.getAllTags(1, 100, prefix || searchTerm)
      .then((response) => {
        if (cancelled) return;
        if (response?.status?.code === 200) {
          const list = response.data?.tags || [];
          const tagNames = list.map((t) => t.tag || t);
          setHierarchyModalTags(tagNames);
        } else {
          setHierarchyModalTags([]);
        }
      })
      .catch(() => {
        if (!cancelled) setHierarchyModalTags([]);
      })
      .finally(() => {
        if (!cancelled) setHierarchyModalTagsLoading(false);
      });
    return () => { cancelled = true; };
  }, [showHierarchyModal, selectedParent]);

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setEditedTagValue(tag);
  };

  const handleSaveTag = async () => {
    if (!editingTag || !editedTagValue.trim()) return;
    
    try {
      const response = await productListApiService.updateTag(editingTag, editedTagValue.trim());
      if (response?.status?.code === 200) {
        await fetchTags();
        setEditingTag(null);
        setEditedTagValue('');
        message.showToast('Tag updated successfully', ToastTypes.SUCCESS);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to update tag', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      message.showToast(error?.message || 'Failed to update tag', ToastTypes.ERROR);
    }
  };

  const handleToggleHideTag = async (tag) => {
    try {
      const response = await productListApiService.toggleHideTag(tag);
      if (response?.status?.code === 200) {
        await fetchTags();
        message.showToast('Tag visibility updated successfully', ToastTypes.SUCCESS);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to toggle tag visibility', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to toggle tag visibility:', error);
      message.showToast(error?.message || 'Failed to toggle tag visibility', ToastTypes.ERROR);
    }
  };

  // Hierarchy management functions
  const openHierarchyModal = (parentTag = '') => {
    setSelectedParent(parentTag);
    if (parentTag) {
      const hierarchy = hierarchies.find(h => h.parentTag === parentTag);
      setSelectedChildren(hierarchy?.childTags || []);
    } else {
      setSelectedChildren([]);
    }
    setShowHierarchyModal(true);
  };

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);


  const handleSaveHierarchy = async () => {
    if (!selectedParent.trim()) {
      message.showToast('Please select a parent tag', ToastTypes.ERROR);
      return;
    }

    setHierarchyLoading(true);
    try {
      // Use new ParentTags API
      const response = await productListApiService.setParentTags(selectedParent, selectedChildren);
      if (response?.status?.code === 200) {
        await fetchHierarchies();
        closeHierarchyModal();
        message.showToast('Parent tags saved successfully', ToastTypes.SUCCESS);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to save parent tags', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to save parent tags:', error);
      message.showToast(error?.message || 'Failed to save parent tags', ToastTypes.ERROR);
    } finally {
      setHierarchyLoading(false);
    }
  };

  const [makingParentTag, setMakingParentTag] = useState(null);

  const handleMakeParentOnly = async (tag) => {
    setMakingParentTag(tag);
    try {
      const response = await productListApiService.setParentTags(tag, []);
      if (response?.status?.code === 200) {
        await fetchHierarchies();
        closeHierarchyModal();
        message.showToast(`"${tag}" is now a parent tag (no children). You can add children later via Edit Parent.`, ToastTypes.SUCCESS);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to set parent tag', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to set parent tag:', error);
      message.showToast(error?.message || 'Failed to set parent tag', ToastTypes.ERROR);
    } finally {
      setMakingParentTag(null);
    }
  };

  const handleDeleteParent = async (parentTag) => {
    if (!window.confirm(`Are you sure you want to delete the parent tag "${parentTag}" and its children?`)) {
      return;
    }

    try {
      // Use new ParentTags API
      const response = await productListApiService.deleteParentTag(parentTag);
      if (response?.status?.code === 200) {
        await fetchHierarchies();
        message.showToast('Parent tag deleted successfully', ToastTypes.SUCCESS);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to delete parent tag', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to delete parent tag:', error);
      message.showToast(error?.message || 'Failed to delete parent tag', ToastTypes.ERROR);
    }
  };

  const toggleChildSelection = (tag) => {
    if (selectedChildren.includes(tag)) {
      setSelectedChildren(selectedChildren.filter(t => t !== tag));
    } else {
      setSelectedChildren([...selectedChildren, tag]);
    }
  };

  // Get available tags for hierarchy (tags that are not already parent tags)
  const availableTags = useMemo(() => {
    const parentTags = hierarchies.map(h => h.parentTag);
    return tags.map(t => t.tag || t).filter(tag => !parentTags.includes(tag));
  }, [tags, hierarchies]);

  // Score for sorting by similarity to parent: lower = more similar
  const getSimilarityRank = (tag, parent) => {
    if (!parent || !tag) return 4;
    const t = tag.toLowerCase();
    const p = parent.toLowerCase();
    const parentPrefix = p.includes('-') ? p.split('-')[0] : p;
    const tagPrefix = t.includes('-') ? t.split('-')[0] : t;
    if (t.startsWith(p) || p.startsWith(t)) return 0;
    if (t.includes(p) || p.includes(t)) return 1;
    if (tagPrefix && parentPrefix && (t.startsWith(parentPrefix) || tagPrefix === parentPrefix)) return 2;
    return 3;
  };

  // Get available child tags: when a parent is selected use similar tags from API, sorted by similarity
  const availableChildTags = useMemo(() => {
    const parentTags = hierarchies.map(h => h.parentTag);
    let childTags;

    if (selectedParent && hierarchyModalTags.length > 0) {
      childTags = hierarchyModalTags.filter(
        (tag) => tag !== selectedParent && !parentTags.includes(tag)
      );
      childTags.sort((a, b) => {
        const rankA = getSimilarityRank(a, selectedParent);
        const rankB = getSimilarityRank(b, selectedParent);
        if (rankA !== rankB) return rankA - rankB;
        return (a || '').localeCompare(b || '');
      });
    } else {
      childTags = tags.map(t => t.tag || t).filter(
        (tag) => tag !== selectedParent && !parentTags.includes(tag)
      );
    }

    if (childTagSearch.trim()) {
      const searchLower = childTagSearch.toLowerCase();
      childTags = childTags.filter((tag) => tag.toLowerCase().includes(searchLower));
    }

    return childTags;
  }, [tags, hierarchies, selectedParent, childTagSearch, hierarchyModalTags]);

  // Get parent tags with their children for the parent tab
  const parentTagsData = useMemo(() => {
    return hierarchies.map(h => {
      const parentTagObj = tags.find(t => (t.tag || t) === h.parentTag);
      const childTagsData = h.childTags.map(childTag => 
        tags.find(t => (t.tag || t) === childTag)
      ).filter(Boolean);
      
      return {
        parentTag: h.parentTag,
        parentTagData: parentTagObj,
        childTags: h.childTags,
        childTagsData: childTagsData
      };
    });
  }, [hierarchies, tags]);

  // Toggle expand/collapse for parent tags
  const toggleParentExpand = (parentTag) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentTag)) {
      newExpanded.delete(parentTag);
    } else {
      newExpanded.add(parentTag);
    }
    setExpandedParents(newExpanded);
  };

  // Open hierarchy modal for a specific tag
  const openHierarchyModalForTag = (tag) => {
    // Check if this tag is already a parent
    const existingHierarchy = hierarchies.find(h => h.parentTag === tag);
    if (existingHierarchy) {
      setSelectedParent(existingHierarchy.parentTag);
      setSelectedChildren(existingHierarchy.childTags || []);
    } else {
      setSelectedParent(tag);
      setSelectedChildren([]);
    }
    setChildTagSearch(''); // Reset search
    setShowHierarchyModal(true);
  };

  const closeHierarchyModal = () => {
    setShowHierarchyModal(false);
    setSelectedParent('');
    setSelectedChildren([]);
    setChildTagSearch('');
  };

  // Search is handled server-side; only status filter is local
  const filteredTags = useMemo(() => {
    return tags.filter(tagObj => {
      const isHidden = tagObj.isHidden || false;
      
      // Status filter
      const matchesStatus = 
        statusFilter === 'all' || 
        (statusFilter === 'visible' && !isHidden) ||
        (statusFilter === 'hidden' && isHidden);
      
      return matchesStatus;
    });
  }, [tags, statusFilter]);

  // Server-side pagination: we only have the current page's tags; show them (filtered within page)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + filteredTags.length;
  const paginatedTags = filteredTags; // One page of data, no slice
  
  const displayTotalPages = totalPages > 0 ? totalPages : 1;
  const displayTotalTags = totalTags > 0 ? totalTags : filteredTags.length;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  return (
    <div className="tags-management-page">
      <div className="tags-header">
        <div className="tags-controls">
          <div className="tags-search">
            <input
              type="text"
              placeholder="Search tags..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className="search-input"
            />
            <button
              type="button"
              onClick={handleSearch}
              className="tags-search-btn"
            >
              Search
            </button>
          </div>
          <div className="tags-filters">
            {activeTab === 'all' && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Tags</option>
                  <option value="visible">Visible Only</option>
                  <option value="hidden">Hidden Only</option>
                </select>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="filter-select"
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tags-tabs">
        <button
          className={`tags-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <FaTag /> All Tags ({totalTags > 0 ? totalTags : tags.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'parent' ? 'active' : ''}`}
          onClick={() => setActiveTab('parent')}
        >
          <FaSitemap /> Parent Tags ({hierarchies.length})
        </button>
      </div>

      <div className="tags-content" style={{ position: 'relative', minHeight: 120 }}>
        <Loader value={loading} variant="overlay" text="Loading tags..." />
        {activeTab === 'all' && (
          <>
            <div className="tags-table-container">
                <table className="tags-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Tag</th>
                      <th>Products</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTags.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="no-data">
                          {searchQuery || statusFilter !== 'all' 
                            ? 'No tags found matching your filters' 
                            : 'No tags found'}
                        </td>
                      </tr>
                    ) : (
                      paginatedTags.map((tagObj, index) => {
                        const tag = tagObj.tag || tagObj;
                        const isHidden = tagObj.isHidden || false;
                        const productCount = tagObj.productCount || 0;
                        const isEditing = editingTag === tag;
                        const isParent = hierarchies.some(h => h.parentTag === tag);
                        // Check if this tag is a child of any parent
                        const parentHierarchy = hierarchies.find(h => h.childTags.includes(tag));
                        const isChild = !!parentHierarchy;
                        const parentTagName = parentHierarchy?.parentTag || null;
                        
                        return (
                          <tr key={index} className={isHidden ? 'hidden-tag-row' : ''}>
                            <td className="index-cell">
                              {startIndex + index + 1}
                            </td>
                            <td className="tag-cell">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editedTagValue}
                                  onChange={(e) => setEditedTagValue(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveTag();
                                    } else if (e.key === 'Escape') {
                                      setEditingTag(null);
                                      setEditedTagValue('');
                                    }
                                  }}
                                  className="tag-edit-input"
                                  autoFocus
                                />
                              ) : (
                                <span className="tag-value">
                                  <a
                                    href={`/admin/tags/${encodeURIComponent(tag)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tag-title-link"
                                    title={`Browse products & create keywords for "${tag}"`}
                                  >
                                    {tag}
                                  </a>
                                  {isParent && <span className="parent-badge">Parent</span>}
                                  {isChild && parentTagName && (
                                    <span className="child-badge" title={`Child of ${parentTagName}`}>
                                      Child of: {parentTagName}
                                    </span>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="count-cell">
                              <span className="product-count">
                                {productCount.toLocaleString()} {productCount === 1 ? 'product' : 'products'}
                              </span>
                            </td>
                            <td className="status-cell">
                              <span className={`status-badge ${isHidden ? 'hidden' : 'visible'}`}>
                                {isHidden ? 'Hidden' : 'Visible'}
                              </span>
                            </td>
                            <td className="actions-cell">
                              {isEditing ? (
                                <div className="action-buttons">
                                  <button
                                    onClick={handleSaveTag}
                                    className="tags-action-btn save-btn"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingTag(null);
                                      setEditedTagValue('');
                                    }}
                                    className="tags-action-btn cancel-btn"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="action-buttons">
                                  <button
                                    onClick={() => handleEditTag(tag)}
                                    className="tags-action-btn edit-btn"
                                    title="Edit tag"
                                  >
                                    <FaEdit /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleToggleHideTag(tag)}
                                    className="tags-action-btn hide-btn"
                                    title={isHidden ? 'Show tag' : 'Hide tag'}
                                  >
                                    {isHidden ? <FaEyeIcon /> : <FaEyeSlash />} {isHidden ? 'Show' : 'Hide'}
                                  </button>
                                  {/* Only show Parent Tag button if tag is not a child */}
                                  {!isChild && (
                                    <button
                                      onClick={() => openHierarchyModalForTag(tag)}
                                      className="tags-action-btn parent-btn"
                                      title={isParent ? 'Edit parent tag' : 'Set as parent tag'}
                                    >
                                      <FaSitemap /> {isParent ? 'Edit Parent' : 'Parent Tag'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            
            {!loading && filteredTags.length > 0 && (
              <div className="tags-pagination-container">
                <div className="tags-info">
                  Showing {startIndex + 1} to {startIndex + paginatedTags.length} of {displayTotalTags} tags
                  {tags.length !== filteredTags.length && (
                    <span className="filtered-info"> (filtered from {tags.length} on this page)</span>
                  )}
                </div>
                <Pagination
                  pages={{
                    currentPage,
                    totalPages: displayTotalPages,
                    totalItems: displayTotalTags
                  }}
                  page={currentPage}
                  setPage={setCurrentPage}
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'parent' && (
          <div className="parent-tags-container">
            {hierarchies.length === 0 ? (
              <div className="empty-state">
                <FaSitemap className="empty-icon" />
                <h3>No Parent Tags</h3>
                <p>Create parent tags by clicking "Parent Tag" button on any tag in the "All Tags" tab.</p>
              </div>
            ) : (
              <div className="parent-tags-list">
                {parentTagsData.map((parentData) => {
                  const isExpanded = expandedParents.has(parentData.parentTag);
                  const parentTagObj = parentData.parentTagData;
                  
                  return (
                    <div key={parentData.parentTag} className="parent-tag-item">
                      <div className="parent-tag-header" onClick={() => toggleParentExpand(parentData.parentTag)}>
                        <div className="parent-tag-info">
                          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                          <FaTag className="parent-icon" />
                          <span className="parent-tag-name">{parentData.parentTag}</span>
                          {parentTagObj && (
                            <>
                              <span className="parent-tag-count">
                                {parentTagObj.productCount?.toLocaleString() || 0} products
                              </span>
                              <span className={`parent-status-badge ${parentTagObj.isHidden ? 'hidden' : 'visible'}`}>
                                {parentTagObj.isHidden ? 'Hidden' : 'Visible'}
                              </span>
                            </>
                          )}
                          <span className="children-count">
                            {parentData.childTags.length} {parentData.childTags.length === 1 ? 'child' : 'children'}
                          </span>
                        </div>
                        <div className="parent-tag-actions" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/admin/tags/${encodeURIComponent(parentData.parentTag)}?parent=true`}
                            className="tags-action-btn map-btn"
                            title="Map category: common keywords from parent + all child tags"
                          >
                            <FaMapMarkedAlt /> Map
                          </Link>
                          <button
                            onClick={() => openHierarchyModalForTag(parentData.parentTag)}
                            className="tags-action-btn edit-btn"
                            title="Edit parent tag"
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteParent(parentData.parentTag)}
                            className="tags-action-btn delete-btn"
                            title="Delete parent tag"
                          >
                            <FaTimes /> Delete
                          </button>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="child-tags-list">
                          {parentData.childTags.length === 0 ? (
                            <div className="no-children">No child tags assigned</div>
                          ) : (
                            parentData.childTags.map((childTag) => {
                              const childTagObj = parentData.childTagsData.find(c => (c.tag || c) === childTag);
                              return (
                                <div key={childTag} className="child-tag-item">
                                  <FaChevronRight className="child-indent" />
                                  <span className="child-tag-name">{childTag}</span>
                                  {childTagObj && (
                                    <>
                                      <span className="child-tag-count">
                                        {childTagObj.productCount?.toLocaleString() || 0} products
                                      </span>
                                      <span className={`child-status-badge ${childTagObj.isHidden ? 'hidden' : 'visible'}`}>
                                        {childTagObj.isHidden ? 'Hidden' : 'Visible'}
                                      </span>
                                    </>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hierarchy Modal */}
      {showHierarchyModal && (
        <div className="modal-overlay" onClick={closeHierarchyModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FaSitemap /> {selectedParent ? 'Edit' : 'Create'} Tag Hierarchy
              </h2>
              <button onClick={closeHierarchyModal} className="modal-close-btn">
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Parent Tag</label>
                <select
                  value={selectedParent}
                  onChange={(e) => {
                    setSelectedParent(e.target.value);
                    setSelectedChildren([]);
                  }}
                  className="form-select"
                  disabled={!!selectedParent && hierarchies.some(h => h.parentTag === selectedParent)}
                >
                  <option value="">Select a parent tag...</option>
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Child Tags ({selectedChildren.length} selected)</label>
                {selectedParent && selectedChildren.length === 0 && (
                  <p className="parent-without-children-hint" style={{ marginBottom: '8px', fontSize: '12px', color: '#059669' }}>
                    You can save this tag as a parent with no children now and add children later.
                  </p>
                )}
                <input
                  type="text"
                  placeholder="Search child tags..."
                  value={childTagSearch}
                  onChange={(e) => setChildTagSearch(e.target.value)}
                  className="form-select"
                  style={{ marginBottom: '8px' }}
                />
                <div className="child-tags-selection">
                  {hierarchyModalTagsLoading && selectedParent ? (
                    <p className="no-tags-msg">Loading similar tags...</p>
                  ) : availableChildTags.length === 0 ? (
                    <p className="no-tags-msg">
                      {childTagSearch ? 'No tags found matching your search' : selectedParent ? 'No similar tags found. You can still save as parent without children.' : 'No tags available. Please select a parent tag first.'}
                    </p>
                  ) : (
                    <>
                      <div className="select-all-controls" style={{ marginBottom: '8px', padding: '6px', background: '#f9fafb', borderRadius: '4px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedChildren.length === availableChildTags.length) {
                              setSelectedChildren([]);
                            } else {
                              setSelectedChildren([...availableChildTags]);
                            }
                          }}
                          className="select-all-btn"
                        >
                          {selectedChildren.length === availableChildTags.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#6b7280' }}>
                          Showing {availableChildTags.length} of {selectedParent && hierarchyModalTags.length > 0 ? hierarchyModalTags.length : tags.length} tags (similar first)
                        </span>
                      </div>
                      <div className="child-tags-checkbox-list">
                        {availableChildTags.map((tag) => {
                          // Check if this tag is already a child of another parent
                          const existingParent = hierarchies.find(h => 
                            h.parentTag !== selectedParent && h.childTags.includes(tag)
                          );
                          const isAlreadyChild = !!existingParent;
                          const existingParentName = existingParent?.parentTag || null;
                          const isSelected = selectedChildren.includes(tag);
                          
                          return (
                            <div key={tag} className={`child-tag-option ${isAlreadyChild ? 'already-child' : ''} ${isSelected && isAlreadyChild ? 'will-be-moved' : ''}`}>
                              <input
                                type="checkbox"
                                id={`child-${tag}`}
                                checked={isSelected}
                                onChange={() => toggleChildSelection(tag)}
                                title={isAlreadyChild ? `This tag is already a child of "${existingParentName}". It will be moved to "${selectedParent}" if selected.` : ''}
                              />
                              <label htmlFor={`child-${tag}`}>
                                <span className="tag-name">{tag}</span>
                                {isAlreadyChild && existingParentName && (
                                  <span className="existing-parent-badge" title={`Currently a child of "${existingParentName}". Will be moved to "${selectedParent}" if selected.`}>
                                    Child of: {existingParentName}
                                  </span>
                                )}
                                {isSelected && isAlreadyChild && (
                                  <span className="move-indicator" title={`Will be moved from "${existingParentName}" to "${selectedParent}"`}>
                                    → Moving
                                  </span>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {selectedChildren.length > 0 && (
                <div className="selected-children-preview">
                  <label>Selected Children:</label>
                  <div className="children-preview-list">
                    {selectedChildren.map((child) => (
                      <span key={child} className="preview-child-tag">
                        {child}
                        <button
                          onClick={() => toggleChildSelection(child)}
                          className="remove-child-btn"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={closeHierarchyModal}
                className="modal-btn cancel-btn"
                disabled={hierarchyLoading}
              >
                Cancel
              </button>
              {selectedParent && (
                <button
                  type="button"
                  onClick={() => handleMakeParentOnly(selectedParent)}
                  className="modal-btn make-parent-btn"
                  disabled={hierarchyLoading || makingParentTag === selectedParent}
                  title="Save this tag as parent with no children"
                >
                  {makingParentTag === selectedParent ? 'Saving...' : 'Make Parent'}
                </button>
              )}
              <button
                onClick={handleSaveHierarchy}
                className="modal-btn save-btn"
                disabled={hierarchyLoading || !selectedParent || selectedChildren.length === 0}
              >
                {hierarchyLoading ? 'Saving...' : 'Save Hierarchy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tags;

